import { Component, EventEmitter, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Observable } from 'rxjs';
import { SnackbarService } from '../../../../services/snackbar.service';
import { SnackbarAlertStatus } from '../../../../utils';
import { SalesTeamManagementApi } from '../../sales-team-management.api';
import { ReportingLine, SalesTeamLevel, SalesTeamNode } from '../../sales-team-management.model';
import {
  initialsOf,
  managerLevelFor,
  parseReportingLineErrors,
  SALES_TEAM_LEVEL_BADGE,
  salesTeamLevel,
} from '../../sales-team-management.util';

/** How the picker closed: saved lines for the success toast, or only a stale tree to reload. */
export type ManagerPickerResult = { lines: ReportingLine[] } | { refresh: true };

const LEVEL_NAME: Record<SalesTeamLevel, { one: string; many: string }> = {
  [SalesTeamLevel.RSM]: { one: 'Regional Sales Manager', many: 'Regional Sales Managers' },
  [SalesTeamLevel.ASM]: { one: 'Area Sales Manager', many: 'Area Sales Managers' },
  [SalesTeamLevel.SO]: { one: 'Sales Officer', many: 'Sales Officers' },
};

/**
 * Picks a manager for one employee (PUT) or several of the same level (bulk-assign). The candidates are
 * built by the page from the loaded tree: active people exactly one level up. Errors stay in the dialog
 * where the admin can act on them; only a vanished employee or a server failure goes to a toast.
 */
@Component({
  selector: 'app-manager-picker-modal',
  standalone: false,
  templateUrl: './manager-picker-modal.component.html',
  styleUrl: './manager-picker-modal.component.scss',
})
export class ManagerPickerModalComponent {
  /** Set by the opener before the first render. */
  employees: SalesTeamNode[] = [];
  bulk: boolean = false;
  /** The employees' current manager when they share one, pre-selected; null for Admin or a mix. */
  currentManagerId: string | null = null;
  /** Current manager's name per employee id, "Admin" when they have none. */
  currentManagerNames: Record<string, string> = {};

  /** Asks the page to reload the tree; the page then updates `candidates`. */
  @Output() refreshRequested: EventEmitter<void> = new EventEmitter<void>();

  protected selectedManagerId: string | null = null;
  protected searchTerm: string = '';
  protected isSaving: boolean = false;
  protected managerErrors: string[] = [];
  protected listErrors: string[] = [];
  protected employeeErrors: Record<string, string[]> = {};
  protected otherErrors: string[] = [];
  /** "2 of 3 couldn't be assigned. No changes were made." */
  protected failureSummary: string = '';

  private _candidates: SalesTeamNode[] = [];

  constructor(
    protected activeModal: NgbActiveModal,
    private _salesTeamApi: SalesTeamManagementApi,
    private _snackbarService: SnackbarService
  ) {}

  get candidates(): SalesTeamNode[] {
    return this._candidates;
  }

  /** Replaced after a tree reload; a picked manager who is no longer a candidate is cleared. */
  set candidates(candidates: SalesTeamNode[]) {
    this._candidates = candidates;

    if (this.selectedManagerId && !candidates.some(candidate => candidate.id === this.selectedManagerId)) {
      this.selectedManagerId = null;
    }
  }

  /** Called by the opener once the inputs are set. */
  init(): void {
    this.selectedManagerId = this.candidates.some(candidate => candidate.id === this.currentManagerId)
      ? this.currentManagerId
      : null;
  }

  protected get title(): string {
    if (this.bulk) {
      return 'Assign manager';
    }

    return this.currentManagerId ? 'Change manager' : 'Assign manager';
  }

  /** The level the picker lists, e.g. "Area Sales Manager" for Sales Officers. */
  protected get managerLevelName(): { one: string; many: string } {
    const level = this.employees[0] ? managerLevelFor(this.employees[0].designation) : null;

    return level ? LEVEL_NAME[level] : { one: 'manager', many: 'managers' };
  }

  /** "Sales Officer" or "Sales Officers", for the bulk heading. */
  protected get employeeLevelName(): string {
    const level = this.employees[0] ? salesTeamLevel(this.employees[0].designation) : null;

    return level ? LEVEL_NAME[level][this.employees.length === 1 ? 'one' : 'many'] : 'employees';
  }

  protected get subtitle(): string {
    if (this.bulk) {
      return `Choose one ${this.managerLevelName.one} for ${this.employees.length} ${this.employeeLevelName}.`;
    }

    return this.employees[0] ? `Choose who ${this.employees[0].name} reports to.` : '';
  }

  protected get selectedManager(): SalesTeamNode | null {
    return this.candidates.find(candidate => candidate.id === this.selectedManagerId) ?? null;
  }

  /** The current manager's name for the single-person route; "Admin" when they have none. */
  protected get currentManagerName(): string {
    return (this.employees[0] && this.currentManagerNames[this.employees[0].id]) || 'Admin';
  }

  /** Says what Save will do, e.g. "Assign to Mahesh Patil" or "Assign 3 to Mahesh Patil". */
  protected get saveLabel(): string {
    const manager = this.selectedManager;

    if (!manager || manager.id === this.currentManagerId) {
      return 'Save';
    }

    return this.bulk ? `Assign ${this.employees.length} to ${manager.name}` : `Assign to ${manager.name}`;
  }

  protected get filteredCandidates(): SalesTeamNode[] {
    return this.searchTerm.trim()
      ? this.candidates.filter(candidate => this.searchCandidate(this.searchTerm, candidate))
      : this.candidates;
  }

  protected initials(node: SalesTeamNode): string {
    return initialsOf(node.name);
  }

  protected levelClassOf(node: SalesTeamNode): string {
    return `level-${salesTeamLevel(node.designation) ?? 'other'}`;
  }

  protected onPick(id: string): void {
    this.selectedManagerId = id;
    this.managerErrors = [];
  }

  protected badgeClassOf(node: SalesTeamNode): string {
    const level = salesTeamLevel(node.designation);

    return level ? SALES_TEAM_LEVEL_BADGE[level].badgeClass : 'badge-soft-secondary';
  }

  /** Save needs a manager different from the current one, someone to assign, and no request in flight. */
  protected get canSave(): boolean {
    return (
      !!this.selectedManagerId &&
      this.selectedManagerId !== this.currentManagerId &&
      this.employees.length > 0 &&
      !this.isSaving
    );
  }

  protected searchCandidate = (term: string, candidate: SalesTeamNode): boolean => {
    const needle = term.trim().toLowerCase().replace(/^#/, '');

    return candidate.name.toLowerCase().includes(needle) || String(candidate.employee_number).includes(needle);
  };

  /** Bulk: drop someone from the selection, for example one the backend refused. */
  protected onRemoveEmployee(id: string): void {
    this.employees = this.employees.filter(employee => employee.id !== id);
    delete this.employeeErrors[id];

    if (Object.keys(this.employeeErrors).length === 0) {
      this.failureSummary = '';
    }
  }

  protected onSave(): void {
    if (!this.canSave) {
      return;
    }

    const managerId = this.selectedManagerId!;
    const employeeIds = this.employees.map(employee => employee.id);
    const request: Observable<ReportingLine | ReportingLine[]> = this.bulk
      ? this._salesTeamApi.bulkAssign(managerId, employeeIds)
      : this._salesTeamApi.assignManager(employeeIds[0], managerId);

    this._clearErrors();
    this.isSaving = true;

    request.pipe(finalize(() => (this.isSaving = false))).subscribe({
      next: result => this.activeModal.close({ lines: Array.isArray(result) ? result : [result] }),
      error: (error: HttpErrorResponse) => this._handleError(error, employeeIds),
    });
  }

  private _handleError(error: HttpErrorResponse, sentEmployeeIds: string[]): void {
    // 401 is handled by the auth interceptor (refresh, or logout when that fails).
    if (error.status === 401) {
      return;
    }

    if (error.status === 0 || error.status >= 500) {
      // Keep the dialog and its selection; the toast offers the same save again.
      this._snackbarService
        .show(
          "Couldn't save the reporting line. Check your connection and try again.",
          SnackbarAlertStatus.ERROR,
          8000,
          'Retry'
        )
        .onAction()
        .subscribe(() => this.onSave());

      return;
    }

    if (error.status === 403) {
      this.otherErrors = ['Only admins can change reporting lines'];

      return;
    }

    if (error.status === 404) {
      this._snackbarService.show('This employee is no longer in the sales team', SnackbarAlertStatus.ERROR);
      this.activeModal.close({ refresh: true });

      return;
    }

    const parsed = parseReportingLineErrors(error.error, sentEmployeeIds);
    const failingCount = Object.keys(parsed.byEmployee).length;

    this.managerErrors = parsed.manager;
    this.listErrors = parsed.list;
    this.employeeErrors = parsed.byEmployee;
    this.otherErrors = parsed.other;
    this.failureSummary = failingCount
      ? `${failingCount} of ${sentEmployeeIds.length} couldn't be assigned. No changes were made.`
      : '';

    if (parsed.refreshTree) {
      this.refreshRequested.emit();
    }
  }

  private _clearErrors(): void {
    this.managerErrors = [];
    this.listErrors = [];
    this.employeeErrors = {};
    this.otherErrors = [];
    this.failureSummary = '';
  }
}
